// src/services/orderService.js
import { supabase } from './supabase'

/**
 * 訂單服務
 * 處理所有訂單相關的 CRUD 操作
 */

// ============================================
// 1. 創建訂單
// ============================================

/**
 * 創建新訂單
 * @param {Object} orderData - 訂單數據
 * @returns {Promise<Object>} 創建結果
 */
export const createOrder = async (orderData) => {
  try {
    const {
      userId,
      bookingMode,
      deliveryAddress,
      items,           // 餐點詳情數組
      subtotal,
      tax = 0,
      deliveryFee = 0,
      discount = 0,
      notes = ''
    } = orderData

    const totalAmount = subtotal + tax + deliveryFee - discount

    const { data, error } = await supabase
      .from('orders')
      .insert({
        user_id: userId,
        booking_mode: bookingMode,
        delivery_address: deliveryAddress,
        items: items,
        subtotal: subtotal,
        tax: tax,
        delivery_fee: deliveryFee,
        discount: discount,
        total_amount: totalAmount,
        status: 'pending',
        payment_status: 'unpaid',
        notes: notes
      })
      .select()
      .single()

    if (error) throw error

    return { success: true, data }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// ============================================
// 2. 查詢訂單
// ============================================

/**
 * 獲取用戶所有訂單
 */
export const getUserOrders = async (userId, options = {}) => {
  try {
    const {
      status = null,      // 篩選狀態
      limit = 50,         // 限制數量
      offset = 0,         // 分頁偏移
      orderBy = 'created_at', // 排序欄位
      ascending = false   // 排序方向
    } = options

    let query = supabase
      .from('orders')
      .select('*')
      .eq('user_id', userId)

    // 如果指定狀態，添加篩選
    if (status) {
      query = query.eq('status', status)
    }

    // 排序和分頁
    query = query
      .order(orderBy, { ascending })
      .range(offset, offset + limit - 1)

    const { data, error, count } = await query

    if (error) throw error

    return { success: true, data, count }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

/**
 * 獲取單個訂單詳情
 */
export const getOrderById = async (orderId) => {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('id', orderId)
      .single()

    if (error) throw error

    return { success: true, data }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

/**
 * 通過訂單號碼查詢
 */
export const getOrderByNumber = async (orderNumber) => {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('order_number', orderNumber)
      .single()

    if (error) throw error

    return { success: true, data }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// ============================================
// 3. 更新訂單
// ============================================

/**
 * 更新訂單狀態
 */
export const updateOrderStatus = async (orderId, status) => {
  try {
    const updates = { status }

    // 記錄狀態變更時間
    if (status === 'preparing') {
      updates.preparing_at = new Date().toISOString()
    }
    if (status === 'ready') {
      updates.ready_at = new Date().toISOString()
    }
    if (status === 'completed') {
      updates.completed_at = new Date().toISOString()
    }

    const { data, error } = await supabase
      .from('orders')
      .update(updates)
      .eq('id', orderId)
      .select()
      .single()

    if (error) throw error

    return { success: true, data }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

/**
 * 更新支付狀態
 */
export const updatePaymentStatus = async (orderId, paymentStatus, paymentData = {}) => {
  try {
    const updates = {
      payment_status: paymentStatus,
      ...paymentData
    }

    // 如果支付成功，記錄支付時間
    if (paymentStatus === 'paid') {
      updates.paid_at = new Date().toISOString()
    }

    const { data, error } = await supabase
      .from('orders')
      .update(updates)
      .eq('id', orderId)
      .select()
      .single()

    if (error) throw error

    return { success: true, data }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

/**
 * 取消訂單
 */
export const cancelOrder = async (orderId, reason = '') => {
  try {
    const { data, error } = await supabase
      .from('orders')
      .update({
        status: 'cancelled',
        notes: reason ? `取消原因: ${reason}` : null
      })
      .eq('id', orderId)
      .in('status', ['pending', 'accepted']) // 允許取消待處理和已接受的訂單
      .select()
      .single()

    if (error) throw error

    return { success: true, data }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// ============================================
// 4. 統計查詢
// ============================================

/**
 * 獲取用戶訂單統計
 */
export const getUserOrderStats = async (userId) => {
  try {
    const { data, error } = await supabase
      .rpc('get_user_order_stats', { user_uuid: userId })
      .single()

    if (error) throw error

    return { success: true, data }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

/**
 * 獲取最近訂單
 */
export const getRecentOrders = async (userId, limit = 5) => {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) throw error

    return { success: true, data }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// ============================================
// 5. 輔助函數
// ============================================

/**
 * 計算訂單總金額
 */
export const calculateOrderTotal = (items, options = {}) => {
  const {
    taxRate = 0.05,      // 5% 稅率
    deliveryFee = 0,     // 配送費
    discount = 0         // 折扣
  } = options

  // 計算小計
  const subtotal = items.reduce((sum, item) => {
    const lunchPrice = item.lunch ? item.lunch.price : 0
    const dinnerPrice = item.dinner ? item.dinner.price : 0
    return sum + lunchPrice + dinnerPrice
  }, 0)

  // 計算稅額
  const tax = subtotal * taxRate

  // 計算總計
  const total = subtotal + tax + deliveryFee - discount

  return {
    subtotal: parseFloat(subtotal.toFixed(2)),
    tax: parseFloat(tax.toFixed(2)),
    deliveryFee: parseFloat(deliveryFee.toFixed(2)),
    discount: parseFloat(discount.toFixed(2)),
    total: parseFloat(total.toFixed(2))
  }
}

/**
 * 格式化訂單項目（從前端狀態轉換為數據庫格式）
 */
export const formatOrderItems = (next7Days, selectedDays, mealSelections, selectedTime, menuOptions) => {
  return selectedDays.map(dayId => {
    const dayInfo = next7Days[dayId]
    const meals = mealSelections[dayId]

    const item = {
      date: dayInfo.date.toISOString().split('T')[0],
      dayId: dayId,
      dateString: dayInfo.dateString,
      weekday: dayInfo.weekday
    }

    // 添加午餐資訊
    if (meals.lunch) {
      const lunchMeal = menuOptions.find(m => m.id === meals.lunch)
      item.lunch = {
        id: lunchMeal.id,
        name: lunchMeal.name,
        price: lunchMeal.price,
        emoji: lunchMeal.emoji
      }
    }

    // 添加晚餐資訊
    if (meals.dinner) {
      const dinnerMeal = menuOptions.find(m => m.id === meals.dinner)
      item.dinner = {
        id: dinnerMeal.id,
        name: dinnerMeal.name,
        price: dinnerMeal.price,
        emoji: dinnerMeal.emoji
      }
    }

    // 添加配送時間
    item.deliveryTime = {
      lunch: selectedTime[`${dayId}_lunch`] || null,
      dinner: selectedTime[`${dayId}_dinner`] || null
    }

    return item
  })
}

/**
 * 驗證訂單數據
 */
export const validateOrderData = (orderData) => {
  const errors = []

  if (!orderData.userId) {
    errors.push('缺少用戶 ID')
  }

  if (!orderData.bookingMode) {
    errors.push('缺少訂單類型')
  }

  if (!orderData.deliveryAddress || !orderData.deliveryAddress.street) {
    errors.push('配送地址不完整')
  }

  if (!orderData.items || orderData.items.length === 0) {
    errors.push('訂單中沒有餐點')
  }

  if (!orderData.subtotal || orderData.subtotal <= 0) {
    errors.push('訂單金額錯誤')
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}